import { TrackInstances } from './TrackInstances';

export type SelectableInstance<T> = {
  selected: boolean;
  select(): void;
  deselect(): void;
  toggleSelection(): void;
  hideOthers(callback?: (instance: T) => void): void;
  showAll(callback?: (instance: T) => void): void;
  isAnySelected(): boolean;
  getOtherInstances?(): T[];
};

export function getSelectable<T>(instance: T): SelectableOf<T> {
  return instance as unknown as SelectableOf<T>;
}

export type SelectableOf<T> = T & SelectableInstance<T>;

/* eslint-disable @typescript-eslint/no-explicit-any */
type Constructor<T = object> = new (...args: any[]) => T;

export function Selectable<T extends Constructor>(Base: T) {
  const Tracked = TrackInstances(Base);

  return class extends Tracked {
    public selected = false;

    select() {
      this.selected = true;
    }

    deselect() {
      this.selected = false;
    }

    toggleSelection() {
      this.selected = !this.selected;
    }

    hideOthers(callback?: (instance: this) => void): void {
      const Class = this.constructor as unknown as {
        // @ts-expect-error there is no other way
        getInstances(): (typeof this & { selected: boolean })[];
      };

      const instances = Class.getInstances();

      instances.forEach((instance) => {
        if (instance !== this && typeof instance.setVisible === 'function') {
          instance.setVisible(false);
          callback?.(instance); // now valid!
        }
      });

      this.select?.();
    }

    showAll(callback?: (instance: this) => void): void {
      const Class = this.constructor as unknown as {
        // @ts-expect-error there is no other way
        getInstances(): (typeof this & { selected: boolean })[];
      };

      const instances = Class.getInstances();

      instances.forEach((instance) => {
        instance.setVisible(true);
        instance.deselect?.();
        callback?.(instance); // also now valid!
      });
    }

    isAnySelected(): boolean {
      const Class = this.constructor as unknown as {
        // @ts-expect-error there is no other way
        getInstances(): (typeof this & { selected: boolean })[];
      };

      const instances = Class.getInstances();

      return instances.some((instance) => instance.selected);
    }

    // Optional method to get other instances of the same type
    getOtherInstances?(): this[] {
      const Class = this.constructor as unknown as {
        // @ts-expect-error there is no other way
        getInstances(): (typeof this & { selected: boolean })[];
      };
      const instances = Class.getInstances();
      return instances.filter((instance) => instance !== this) as this[];
    }
  };
}
